import type { ZudokuConfig } from "zudoku";

/**
 * docs.vploq.com — every public vploq document lives here.
 *
 * The first tenant is the legal set (privacy policy, terms), which is why
 * two things below are not defaults and should not be "tidied up":
 *
 *   - `prerender` is load-bearing. A privacy policy has to be readable by a
 *     regulator, a screen reader and a ten-year-old phone, so the text must be
 *     in the served HTML rather than assembled by script after load.
 *   - `canonicalUrlOrigin` is fixed. The address is compiled into shipped app
 *     binaries and quoted in both store listings; it cannot move without a
 *     store release, so nothing here may make the served URL depend on where
 *     the site happens to be deployed.
 */
const config: ZudokuConfig = {
  site: {
    title: "vploq Docs",
    // Served from public/ rather than the Shopify CDN the mark is also
    // published on. This site carries a legal notice: it should not stop
    // rendering correctly because a storefront moved an asset or a network
    // blocks that host.
    // Two files, because the wordmark is baked into the SVG's fills and the
    // logo is rendered as an <img> — which cannot inherit the page's text
    // colour. `light` is the asset shown on the light theme, so it carries the
    // ink wordmark; both pointed at the white one, which left the word
    // invisible on white and only the purple mark showing.
    logo: {
      src: { light: "/vploq-logo-light.svg", dark: "/vploq-logo.svg" },
      alt: "vploq",
      width: "104px",
    },
    showPoweredBy: false,
  },
  metadata: {
    title: "%s | vploq",
    defaultTitle: "vploq Docs",
    description:
      "Legal notices and product documentation for vploq — the home network you take with you.",
    // The icon half of the brand mark, cropped out of the same logo the header
    // uses and served from public/ rather than the storefront CDN — for the
    // reason given on site.logo above.
    favicon: "/favicon.svg",
  },
  canonicalUrlOrigin: "https://docs.vploq.com",
  docs: {
    files: "/pages/**/*.{md,mdx}",
  },
  // File-based routing would serve pages/home.mdx at /home; this binds it to
  // the root instead. Zudoku's own guidance is to avoid naming a file
  // index.mdx and to bind the root through `path`, because hosts that strip
  // /index from URLs otherwise leave one page reachable at two addresses —
  // and a legal notice with two addresses is a citation problem.
  navigation: [
    {
      type: "doc",
      file: "home",
      path: "/",
      label: "Home",
    },
    {
      // The landing is no longer a page of this SPA. It ships as a single
      // self-contained HTML file (built by vite.landing.config.ts) served at
      // download.vploq.com's bare root, so that no docs router can grab "/"
      // and redirect the landing to the docs Home. The docs nav keeps a
      // "Download" tab for discoverability, now an external link to that site.
      type: "link",
      to: "https://download.vploq.com",
      label: "Download",
    },
    {
      type: "category",
      label: "Learn",
      items: ["learn/how-vploq-works"],
    },
    {
      type: "category",
      label: "Setup",
      items: ["setup/install-the-app", "setup/set-up-your-vploq"],
    },
    {
      type: "category",
      label: "Using vploq",
      items: [
        "using/connecting",
        "using/local-network-access",
        "using/your-device",
      ],
    },
    {
      type: "category",
      label: "Teams",
      items: ["teams/managing-teams"],
    },
    {
      type: "category",
      label: "Account",
      items: ["account/your-data"],
    },
    {
      type: "category",
      label: "Support",
      items: ["support/troubleshooting", "support/faq", "support/security-updates"],
    },
    {
      type: "category",
      label: "Legal",
      items: ["privacy", "legal/security", "legal/overview"],
    },
  ],
  search: {
    type: "pagefind",
  },
};

export default config;
